import { useMemo, type ReactElement } from 'react';
import { componentSizes } from '@/theme/componentSizes';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { MetricCard } from '@/ui/composites';
import { Stack } from '@/ui/layout/Stack';
import { Box, Skeleton, Surface, useTheme } from '@/ui/primitives';
import { type MetricCardItem } from './statusMetricCards';

type StatusMetricCardsRowProps = {
  cards: MetricCardItem[];
  selectedIds?: string[];
  onToggle?: (id: string) => void;
  loading?: boolean;
  skeletonCount?: number;
};

type StatusMetricCardsRowSkeletonProps = {
  count?: number;
};

const StatusMetricCardsRowSkeleton = ({
  count = 5,
}: StatusMetricCardsRowSkeletonProps): ReactElement => (
  <Stack direction="row" wrap="wrap" gap="md" align="stretch">
    {Array.from({ length: count }).map((_, index) => (
      <Box
        key={index}
        sx={{
          width: { xs: '100%', sm: s('section') },
          flex: { xs: '0 0 100%', sm: `0 0 ${s('section')}` },
        }}
      >
        <Surface
          padding="none"
          border={false}
          sx={{
            minHeight: componentSizes.cards.statusHeight,
            height: componentSizes.cards.statusHeight,
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          <Skeleton variant="rounded" width="100%" height="100%" />
        </Surface>
      </Box>
    ))}
  </Stack>
);

const isPlaceholderCard = (card: MetricCardItem): boolean =>
  card.count === '-' && (card.percentage === '-' || card.percentage === undefined);

export const StatusMetricCardsRow = ({
  cards,
  selectedIds,
  onToggle,
  loading,
  skeletonCount,
}: StatusMetricCardsRowProps): ReactElement => {
  const theme = useTheme();
  const accentColor = resolveSemanticColor(theme, 'accent');
  const selectedSet = useMemo(() => new Set(selectedIds ?? []), [selectedIds]);
  const inferredLoading = cards.length > 0 && cards.every(isPlaceholderCard);
  const shouldShowSkeleton = loading ?? inferredLoading;
  const animationKey = useMemo(() => {
    const toKeyPart = (value: unknown): string => {
      if (value === null || value === undefined) return '';
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
      }
      return '';
    };
    return cards
      .map((card) =>
        [
          card.id,
          toKeyPart(card.status),
          card.statusTone,
          toKeyPart(card.count),
          toKeyPart(card.percentage),
          toKeyPart(card.delta),
          card.deltaTone ?? '',
        ].join(':'),
      )
      .join('|');
  }, [cards]);
  if (shouldShowSkeleton) {
    const resolvedCount = skeletonCount ?? (cards.length > 0 ? cards.length : 5);
    return <StatusMetricCardsRowSkeleton count={resolvedCount} />;
  }
  const restoreScroll = (): void => {
    const scrollY = typeof window !== 'undefined' ? window.scrollY : null;
    if (scrollY === null) return;
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY, behavior: 'auto' });
      });
    } else {
      window.setTimeout(() => {
        window.scrollTo({ top: scrollY, behavior: 'auto' });
      }, 0);
    }
  };
  return (
    <Box key={animationKey} className="flow-card-enter">
      <Stack
        direction="row"
        wrap="wrap"
        gap="md"
        align="stretch"
        justify={{ xs: 'center', sm: 'flex-start' }}
      >
        {cards.map((card) => {
          const isSelectable = Boolean(onToggle) && card.id !== 'total';
          const isSelected = selectedSet.has(card.id);
          return (
            <Box
              key={card.id}
              component={isSelectable ? 'button' : 'div'}
              type={isSelectable ? 'button' : undefined}
              onClick={
                isSelectable
                  ? () => {
                      onToggle?.(card.id);
                      restoreScroll();
                    }
                  : undefined
              }
              aria-pressed={isSelectable ? isSelected : undefined}
              sx={[
                {
                  width: { xs: '100%', sm: 'auto' },
                  flex: { xs: '0 0 100%', sm: '0 0 auto' },
                },
                isSelectable
                  ? {
                      padding: s('none'),
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      '&:focus-visible > div': {
                        outline: `2px solid ${accentColor}`,
                        outlineOffset: s('xxs'),
                      },
                    }
                  : null,
              ]}
            >
              <MetricCard
                status={card.status}
                statusTone={card.statusTone}
                count={card.count}
                percentage={card.percentage}
                delta={card.delta}
                deltaTone={card.deltaTone}
                surfaceSx={[
                  {
                    textAlign: { xs: 'center', sm: 'left' },
                    px: s('lg'),
                    py: s('md'),
                  },
                  isSelected ? { borderColor: accentColor } : null,
                ]}
              />
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};
